import { Clock } from "lucide-react";

import { formatArabicDate, readingTimeLabel } from "@/lib/blog/format";
import type { BlogPostSummary } from "@/lib/blog/snapshot";
import { BlogAnchor } from "./BlogAnchor";
import { ClusterChip } from "./ClusterChip";

/**
 * Post card for /blog and «اقرا كمان». The whole card is one BlogAnchor, so the click fires
 * blog_link_clicked with the surrounding BlogPostProvider's surface (blog_index or blog_post).
 * `featured` = the pillar on /blog (more padding, unclamped description); `compact` drops the
 * description (related posts). No images.
 */
export function PostCard({
  post,
  featured = false,
  compact = false,
}: {
  post: BlogPostSummary;
  featured?: boolean;
  compact?: boolean;
}) {
  const className = [
    "block bg-white border border-gray-200 rounded-xl p-4 md:p-6 shadow-sm hover:border-warmOrange-500 transition-colors min-h-11 no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warmOrange-500 motion-reduce:transition-none font-arabic text-start",
    featured ? "md:p-8" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <BlogAnchor href={`/blog/${post.slug}`} className={className}>
      <ClusterChip cluster={post.cluster} />
      <span className="block text-xl font-bold text-gray-900 mt-2">{post.title}</span>
      {!compact && (
        <span className={`block text-base text-gray-600 mt-2${featured ? "" : " line-clamp-2"}`}>
          {post.description}
        </span>
      )}
      <span className="text-sm text-gray-500 mt-4 flex flex-wrap gap-2 items-center">
        <span>{formatArabicDate(post.date)}</span>
        <span aria-hidden="true" className="text-gray-300">
          ·
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-4 w-4" aria-hidden="true" />
          {readingTimeLabel(post.readingMinutes)}
        </span>
      </span>
    </BlogAnchor>
  );
}

export default PostCard;
